"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { type EncodedAudio } from "@/lib/audio/encoding";
import { RecordingLossConfirmModal } from "./RecordingLossConfirmModal";

// Import extracted hooks
import { useDebugMode } from "./hooks/useDebugMode";
import { useNavigationWarnings, setNavigationConfirmed } from "./hooks/useNavigationWarnings";
import {
  useAudioRecording,
  type RecordingState,
  type RecordingMode,
} from "./hooks/useAudioRecording";
import { isUrgencyThresholdReached } from "./hooks/useRecordingTimer";
import {
  useAudioProcessing,
  type PreviewSource,
} from "./hooks/useAudioProcessing";

// Import extracted UI components
import { RecordButton } from "./RecordButton";
import { RecordingTimer } from "./RecordingTimer";
import { PreviewPlayer } from "./PreviewPlayer";
import { DebugOverlay } from "./DebugOverlay";
import { ErrorAlert } from "./ErrorAlert";
import { ActionButtons } from "./ActionButtons";

// Re-export for backwards compatibility
export { setNavigationConfirmed };

type RecorderPanelProps = {
  minDurationSeconds?: number;
  maxDurationSeconds?: number;
  maxSizeMB?: number;
  onPublish?: (audio: EncodedAudio) => Promise<void> | void;
  onBeforeStart?: () => Promise<boolean | void>;
  onRateLimitError?: (error: string) => void;
  submitButtonText?: string;
  submitButtonTextLoading?: string;
  onStateChange?: (state: RecordingState) => void;
  onNavigationAttempt?: (href: string) => void;
  disableBeforeUnload?: boolean;
  hasPendingNavigation?: boolean;
};

const DEFAULT_MIN = 3;
const DEFAULT_MAX = 120;
const DEFAULT_MAX_SIZE_MB = 5;

/**
 * Detect if we're on Android Chrome or Samsung Internet
 * These browsers have issues with DSP flags causing muffled audio
 */
function isAndroidChrome(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  const isChrome = /Chrome/i.test(ua) && !/EdgA|Edg/i.test(ua);
  const isSamsungInternet = /SamsungBrowser/i.test(ua);
  return isAndroid && (isChrome || isSamsungInternet);
}

export function RecorderPanel({
  minDurationSeconds = DEFAULT_MIN,
  maxDurationSeconds = DEFAULT_MAX,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  onPublish,
  onBeforeStart,
  onRateLimitError,
  submitButtonText = "Create Post",
  submitButtonTextLoading = "Creating Post…",
  onStateChange,
  onNavigationAttempt,
  disableBeforeUnload = false,
  hasPendingNavigation = false,
}: RecorderPanelProps) {
  // ============ State ============
  const [state, setState] = useState<RecordingState>("idle");
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [encodedAudio, setEncodedAudio] = useState<EncodedAudio | null>(null);
  const [previewSources, setPreviewSources] = useState<PreviewSource[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [stoppedAtTimeLimit, setStoppedAtTimeLimit] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [lastRecordingBlob, setLastRecordingBlob] = useState<Blob | null>(null);
  const [recordingMode, setRecordingMode] = useState<RecordingMode>(() => {
    // Initialize based on platform
    return isAndroidChrome() ? "raw" : "processed";
  });

  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const stoppedAtTimeLimitRef = useRef(false);
  const preferredMimeTypeRef = useRef<string | null>(null);
  const cancelNextRecordingRef = useRef(false);
  const cancelInProgressRef = useRef(false);

  // ============ Debug Mode Hook ============
  const {
    debugMode,
    mounted,
    debugInfo,
    setDebugInfo,
    log,
  } = useDebugMode();

  // ============ Audio Processing Hook ============
  const processing = useAudioProcessing({
    constraints: {
      minDurationSeconds,
      maxDurationSeconds,
      maxSizeMB,
    },
    debugMode,
    stoppedAtTimeLimitRef,
    preferredMimeTypeRef,
    onProcessingStart: () => {
      setState("processing");
    },
    onProcessingComplete: (result) => {
      setEncodedAudio(result.encodedAudio);
      setPreviewSources(result.previewSources);
      setDebugInfo(result.debugInfo);
      setLastRecordingBlob(result.rawBlob);
      setError(null);
      setState("preview");
    },
    onProcessingError: (errorMsg) => {
      setError(errorMsg);
      setState("idle");
    },
  });

  // ============ Audio Recording Hook ============
  const recording = useAudioRecording({
    maxDurationSeconds,
    recordingMode,
    debugMode,
    onBeforeStart,
    onRateLimitError,
    onStateChange: (newState) => {
      setState(newState);
      onStateChange?.(newState);
    },
    onBlobRecorded: (blob) => {
      if (cancelNextRecordingRef.current || cancelInProgressRef.current) {
        cancelNextRecordingRef.current = false;
        cancelInProgressRef.current = false;
        return;
      }
      void processing.processBlob(blob);
    },
    onPcmRecorded: (pcmData, sampleRate) => {
      if (cancelNextRecordingRef.current || cancelInProgressRef.current) {
        cancelNextRecordingRef.current = false;
        cancelInProgressRef.current = false;
        return;
      }
      void processing.processPcm(pcmData, sampleRate);
    },
    onError: (errorMsg) => {
      if (cancelInProgressRef.current) {
        cancelNextRecordingRef.current = false;
        cancelInProgressRef.current = false;
        return;
      }
      setError(errorMsg);
    },
    onTimerUpdate: (seconds) => {
      setTimerSeconds(Math.min(seconds, maxDurationSeconds));
    },
    onStoppedAtTimeLimit: () => {
      stoppedAtTimeLimitRef.current = true;
      setStoppedAtTimeLimit(true);
    },
  });

  // Sync refs from recording hook
  useEffect(() => {
    if (recording.stoppedAtTimeLimitRef) {
      stoppedAtTimeLimitRef.current = recording.stoppedAtTimeLimitRef.current;
    }
    if (recording.preferredMimeTypeRef) {
      preferredMimeTypeRef.current = recording.preferredMimeTypeRef.current;
    }
  });

  // ============ Navigation Warnings Hook ============
  useNavigationWarnings({
    enabled:
      state === "recording" ||
      state === "processing" ||
      state === "preview" ||
      state === "uploading",
    onNavigationAttempt,
    disableBeforeUnload: disableBeforeUnload || hasPendingNavigation,
  });

  // ============ Notify parent of state changes ============
  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  // ============ Cleanup on unmount ============
  useEffect(() => {
    return () => {
      recording.stopStreams();
      processing.clearPreviewUrls();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ Computed Values ============
  const canPublish = state === "preview" && !publishing && !!encodedAudio;

  const formatTime = useCallback((seconds: number) => {
    const clamped = Math.max(0, seconds);
    const m = Math.floor(clamped / 60);
    const s = Math.floor(clamped % 60);
    const padded = s.toString().padStart(2, "0");
    return `${m}:${padded}`;
  }, []);

  const elapsedFormatted = formatTime(timerSeconds);
  const maxFormatted = formatTime(maxDurationSeconds);

  // Show urgency cue when 10 seconds or less remain
  const URGENCY_THRESHOLD_SECONDS = 10;
  const isUrgent =
    state === "recording" &&
    isUrgencyThresholdReached(
      timerSeconds,
      maxDurationSeconds,
      URGENCY_THRESHOLD_SECONDS
    );

  // ============ Handlers ============
  const resetState = useCallback(() => {
    recording.stopStreams();
    processing.clearPreviewUrls();
    setTimerSeconds(0);
    setError(null);
    stoppedAtTimeLimitRef.current = false;
    setStoppedAtTimeLimit(false);
    setEncodedAudio(null);
    setPreviewSources([]);
    setState("idle");
  }, [recording, processing]);

  const toggleRecording = useCallback(() => {
    if (state === "recording") {
      recording.stopRecording();
    } else {
      setError(null);
      cancelNextRecordingRef.current = false;
      void recording.startRecording();
    }
  }, [state, recording]);

  const handleConfirmCancel = useCallback(() => {
    cancelNextRecordingRef.current = true;
    cancelInProgressRef.current = true;
    setShowCancelModal(false);
    recording.stopRecording();
    recording.stopStreams();
    processing.clearPreviewUrls();
    setTimerSeconds(0);
    setError(null);
    stoppedAtTimeLimitRef.current = false;
    setStoppedAtTimeLimit(false);
    setEncodedAudio(null);
    setPreviewSources([]);
    setState("idle");
  }, [recording, processing]);

  const handleDiscard = useCallback(() => {
    if (state === "preview") {
      setShowDiscardModal(true);
    }
  }, [state]);

  const handlePublish = useCallback(async () => {
    if (!encodedAudio) {
      log("handlePublish called but no encodedAudio");
      return;
    }

    setError(null);
    setPublishing(true);
    setState("uploading");

    try {
      if (onPublish) {
        await onPublish(encodedAudio);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 600));
      }
      resetState();
    } catch (err) {
      console.error("Publish error:", err);
      const message =
        err instanceof Error && err.message
          ? err.message
          : "Publishing failed. Please try again.";
      setError(message);
      setState("preview");
    } finally {
      setPublishing(false);
    }
  }, [encodedAudio, onPublish, resetState, log]);

  const handlePlayLastRecording = useCallback(() => {
    if (lastRecordingBlob) {
      const url = URL.createObjectURL(lastRecordingBlob);
      const audio = new Audio(url);
      audio.play().catch(console.error);
      audio.onended = () => URL.revokeObjectURL(url);
    }
  }, [lastRecordingBlob]);

  // ============ Render ============
  return (
    <div className="space-y-4">
      {/* Debug Overlay */}
      {mounted && debugMode && (
        <DebugOverlay
          debugInfo={debugInfo}
          recordingMode={recordingMode}
          onToggleMode={() =>
            setRecordingMode((m) => (m === "raw" ? "processed" : "raw"))
          }
          onPlayLastRecording={handlePlayLastRecording}
          hasLastRecording={!!lastRecordingBlob}
          urlHasDebugParam={debugMode}
        />
      )}

      {/* Recording Panel */}
      {state !== "preview" && (
        <div className="rounded-2xl border border-[#191927] bg-gradient-to-b from-[#202036] to-[#050509] px-4 pb-3 pt-6 text-center">
          <div
            className={`flex flex-col items-center ${
              state === "processing" || state === "uploading"
                ? "min-h-[150px] justify-center"
                : ""
            }`}
          >
            {state === "idle" && (
              <RecordButton
                isRecording={false}
                isDisabled={publishing}
                onClick={toggleRecording}
              />
            )}
            {state === "recording" && (
              <>
                <RecordButton
                  isRecording
                  isDisabled={false}
                  onClick={toggleRecording}
                />
                <RecordingTimer
                  formattedTime={`${elapsedFormatted} / ${maxFormatted}`}
                  visible
                  isUrgent={isUrgent}
                />
                <button
                  type="button"
                  onClick={() => setShowCancelModal(true)}
                  className="mt-5 rounded-full border border-[#262636] bg-[#0a0a12] px-4 py-2 text-xs font-semibold text-[#a3a3c2] transition hover:border-[rgba(255,255,255,0.16)]"
                >
                  Cancel
                </button>
              </>
            )}
            {state === "processing" && (
              <p className="mt-3 w-full text-center text-[16px] font-medium text-white/70 animate-pulse">
                Processing…
              </p>
            )}
            {state === "uploading" && (
              <p className="mt-3 w-full text-center text-[16px] font-medium text-white/70 animate-pulse">
                Uploading…
              </p>
            )}
          </div>
        </div>
      )}

      {/* Preview Player */}
      {state === "preview" && previewSources.length > 0 && (
        <PreviewPlayer
          ref={audioElementRef}
          sources={previewSources}
          debugMode={debugMode}
        />
      )}

      {/* Error Alert */}
      <ErrorAlert
        error={error}
        onDismiss={() => setError(null)}
        submitButtonText={submitButtonText}
      />

      {/* Stopped at Time Limit Notice */}
      {stoppedAtTimeLimit && !error && (
        <div className="rounded-2xl border border-blue-400/20 bg-blue-500/5 p-3 text-sm text-blue-200/80">
          Recording stopped at 2 minutes.
          <button
            onClick={() => setStoppedAtTimeLimit(false)}
            className="ml-2 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Action Buttons */}
      {state === "preview" && (
        <ActionButtons
          onDiscard={handleDiscard}
          onPublish={() => void handlePublish()}
          canDiscard={state === "preview"}
          canPublish={canPublish}
          isPublishing={publishing}
          publishButtonText={submitButtonText}
          publishButtonTextLoading={submitButtonTextLoading}
        />
      )}

      <RecordingLossConfirmModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel recording?"
        description="Your current recording will be discarded."
        confirmButtonText="Discard"
        cancelButtonText="Keep recording"
      />

      <RecordingLossConfirmModal
        isOpen={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onConfirm={() => {
          setShowDiscardModal(false);
          resetState();
        }}
        title="Discard recording?"
        description="This recording will be discarded."
        confirmButtonText="Discard"
        cancelButtonText="Keep"
      />
    </div>
  );
}

// ============ Utility Components (kept for backwards compatibility) ============

const bars = [12, 26, 18, 36, 20, 28, 16, 32];

function Waveform({ isActive }: { isActive: boolean }) {
  return (
    <div className="flex h-16 items-end justify-center gap-1">
      {bars.map((height, idx) => (
        <span
          key={`${height}-${idx}`}
          className="wave-bar w-1.5 rounded-full bg-gradient-to-t from-white/60 to-white/40"
          data-paused={!isActive}
          style={{ animationDelay: `${idx * 0.08}s`, height: `${height}px` }}
        />
      ))}
    </div>
  );
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs uppercase tracking-wide text-white/80">
      {children}
    </span>
  );
}
